import { useEffect, useState, useContext, useMemo } from 'react';
import PostApi from '../../../api/PostApi';
import { useParams, useNavigate } from 'react-router-dom';
import TimestampDisplay from '../../common/TimestampDisplay';
import DOMPurify from 'dompurify';
import UserContext from '../../../context/UserContext';
import Comment from './Comment';
import { isUsingContactInfo, isUsingReaction, isUsingComment, getPostNicknm, getPostBaseUrl, getPostCategoryName } from '../../../constant/PostClass';
import { loginAlert } from '../../common/CustomAlert';
import { resolvePostProfileImg } from '../../../util/ProfileUtil';

const PostDetail = props => {
    const navigate = useNavigate()
    const { user } = useContext(UserContext);
    const { postId } = useParams();
    const [post, setPost] = useState(null);

    const handleDeleteBtn = async () => {
        const confirmed = window.confirm("삭제하시겠습니까?");
        if (!confirmed) return;
        await PostApi.deletePost(postId);
        navigate('/?reload=true');
    }

    const handleWriteBtn = () => {
        if (!user || !user.userId) return loginAlert('로그인이 필요합니다.');
        navigate(`/post/write?postType=${post.post_type}`);
    }

    const handleBannerClick = url => {
        const newWindow = window.open(url, "_blank");
        if (newWindow) newWindow.focus();
        else alert("팝업 차단 설정이 활성화되어 새 창을 열 수 없습니다.");
    }

    const handleReactionClick = async type => {
        const data = await PostApi.updatePostReaction(postId, type);
        if (data.code === 'fail') return alert(data.message);
        const newPost = {...post};
        if (type === 'like') newPost.post_like_cnt++;
        if (type === 'hate') newPost.post_hate_cnt++;
        if (type === 'report') newPost.post_report_cnt++;
        setPost(newPost);
    }

    useEffect(() => {
        PostApi.getPostFull(postId).then(rslt => {
            if (rslt.post) {
                setPost(rslt.post);
                PostApi.updateVisitCount(postId);
            }
            else alert('존재하지 않은 게시물입니다.');
        });
    }, [postId]);

    // 저장된 본문의 링크를 새 창에서 열리도록 설정 (렌더링 전에 HTML 문자열 처리)
    const sanitizedContent = useMemo(() => {
        if (!post || !post.content) return '';
        
        let content = post.content;
        // 모든 <a> 태그에 target="_blank" 추가 (이미 있는 경우 제외)
        content = content.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
            // 이미 target 속성이 있으면 그대로 반환
            if (/target\s*=/i.test(attributes)) {
                return match;
            }
            // target 속성이 없으면 추가
            return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
        });
        
        // DOMPurify 설정: target 속성 허용
        return DOMPurify.sanitize(content, {
            ADD_ATTR: ['target']
        });
    }, [post?.content]);

    if (!post || !post.post_id) return '';

    return (
        <>
            <div class="section-area">
                <div class="section-content">
                    <div class="bbs-detail-header">
                        <div class="bbs-detail-type">{getPostCategoryName(post)}</div>
                        <h1 class="bbs-detail-subject">{post.post_title}</h1>
                        <div class="bbs-detail-profile">
                            <div class="user-profile">
                                <div class="user-profile-thumb">
                                    <img src={resolvePostProfileImg(post)} />
                                </div>
                                <span class="user-profile-name">{getPostNicknm(post)}</span>
                            </div>
                            <span class="bbs-detail-day"><TimestampDisplay timestamp={post.post_reg_date} /></span>
                        </div>
                    </div>
                    
                    {isUsingContactInfo(post) && <div class="bbs-detail-info">
                        <dl>
                            <dt>회사명</dt>
                            <dd>{post.post_company || '-'}</dd>
                        </dl>
                        <dl>
                            <dt>담당자명</dt>
                            <dd>{post.post_contact || '-'}</dd>
                        </dl>
                        <dl>
                            <dt>연락처</dt>
                            <dd><a>{post.post_contact_phone || '-'}</a></dd>
                        </dl>
                        <dl>
                            <dt>이메일</dt>
                            <dd><a>{post.post_contact_email || '-'}</a></dd>
                        </dl>
                        <dl>
                            <dt>카카오톡</dt>
                            <dd>{post.post_contact_kakao || '-'}</dd>
                        </dl>
                    </div>}

                    <div class="bbs-detail-content">
                        <div class="description ck-editor-content "
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        ></div>
                    </div>

                    {isUsingReaction(post) && 
                        <div class="bss-detail-tool">
                            <button type="button" onClick={() => handleReactionClick('like')}>
                                <i class="icon icon-like"></i>
                                <span>좋아요 {post.post_like_cnt}</span>
                            </button>
                        </div>
                    }

                    <div class="bbs-detail-nav">
                        <a class="btn" onClick={() => navigate(getPostBaseUrl(post))}>
                            <span>목록</span>
                        </a>
                        {post.post_user_id === user.userId && <>
                                <button type="button" class="btn" onClick={() => navigate(`/post/write/${postId}?postType=${post.post_type}`)} >
                                    <span>수정</span>
                                </button>
                                <button type="button" class="btn btn-red" onClick={handleDeleteBtn} >
                                    <span>삭제</span>
                                </button>
                        </>}
                        <button type="button" class="btn btn-primary" onClick={handleWriteBtn}>
                            <span>글쓰기</span>
                        </button>
                    </div>
                </div>
            </div>
            {isUsingComment(post) && <Comment postId={post.post_id} postType={post.post_type} />}
        </>
    )
}

export default PostDetail;